import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { MODES, MODE_ID, nextMode, OMEGA, RETURN_START, SNAP_START, SNAP_BEATS } from './lib/dissolve-plan.js';

// Contrat du rig partagé : interdit. La pochette est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'dissolve',
  family: 'particles',
  danceability: 0.84,
  requiresTier: 0,
  energyLevel: 0.78,
  bpmMin: 100,
  bpmMax: 160,
  movement: 0.85,
  tonality: 0.55,
  prefers: { gridStrength: [0.5, 1.0] },
  enabled: true
};

// DISSOLVE : la pochette en grand au centre de l'écran (R19, 70 % de la
// hauteur), faite de dizaines de milliers de particules, une par cellule
// échantillonnée. Sur la musique elle se DÉSINTÈGRE (sable au vent,
// explosion, tourbillon, pluie, blocs qui se retournent, effritement) puis
// chaque particule revient EXACTEMENT à sa place. EDM, house, trance, pop,
// hip-hop. Règles R1 à R19, preset party de vdjLook, « R20 » : l'image est
// formée et nette dès la première image.
//
// LA SIMULATION, sur le GPU (R15) : deux textures flottantes N x N en
// ping-pong (position xyz + drapeau, vitesse xyz + délai), mises à jour en un
// passage MRT. L'origine d'une particule se déduit de son index (grille sur
// l'image, 1,4 unité de haut, largeur selon la pochette) et sa couleur est lue
// dans la pochette au moment du rendu : un changement de pochette ne touche
// pas la simulation. Une MESURE : dissolution sur les deux premiers temps -
// chaque particule part à son DÉLAI (selon le mode : d'un bord à l'autre pour
// le vent, du centre pour l'explosion, du haut pour le sable...) puis subit
// les forces du mode ; retour sur les deux suivants - un ressort critique
// (ω = 5,5) déclenché avec un délai lié à la distance à l'origine (l'image se
// reforme en vague), puis un RECALAGE exact sur les 0,3 derniers temps : au
// temps fort, p = origine, v = 0, sans exception. PIXELS est cinématique : les
// blocs 16 x 16 tournent autour de leur centre puis se déroulent.
//
// LE RENDU (corrigé le 17/10) : des points. Formée, chaque particule est un
// CARRÉ dont le centre est CALÉ sur la grille des pixels et la taille arrondie
// au pixel supérieur : les carrés pavent l'image sans trou, sans chevauchement
// visible, sans grain. Sa couleur est la MOYENNE de sa cellule, lue dans une
// réduction N x N de la pochette (mipmaps), jamais un seul texel ; les styles
// (néon monochrome, pointillé) ne touchent que les particules en vol. L'onde du
// temps est une ondulation de LUMINANCE (un décalage en profondeur rétrécissait
// des rangées de carrés et laissait des lignes). Quand toutes les particules
// sont à leur place (recalage fini, pas de montée, pas de drop, ou ?dissolvehold),
// la pochette est dessinée en UN quad texturé, parfaitement nette, et les
// points sont masqués ; les points reprennent au premier temps de la mesure.
// En vol, un point rond lumineux, caméra perspective virtuelle (champ 30°).
//
// LE FOND est redessiné entièrement à chaque image, sans aucun feedback : la
// pochette en mode couvrir (R18), floutée UNE fois par pochette par une chaîne
// de réductions linéaires (1/2, 1/4, 1/8) puis un flou gaussien séparable,
// agrandie en LINEAR (aucun bloc), assombrie à 30 %, vignettée. Les TRAÎNÉES des
// particules en vol vivent dans un buffer séparé à demi-résolution, composé
// par-dessus le fond, qui décroît vraiment (c = max(0, c x 0,88 − 2/255) : les
// résidus 8 bits ne stagnent pas) et se vide en 0,3 s dès que l'image est formée.
// Cible privée RGBA8 pour la mesure de confort (R10).
//
// LE RYTHME (party) : TEMPS - une onde de profondeur traverse l'image formée
// (décalage en z au rendu), séparation RGB ; MESURE - l'automate choisit la
// désintégration (jamais deux fois la même), à-coup de caméra ; PHRASE - le
// style de rendu (couleur pleine, monochrome néon, pointillé) ; MONTÉE -
// l'image vibre et des particules se détachent de plus en plus ; DROP -
// EXPLOSION totale, nuage 3D qui tourne deux mesures, retour sur le dernier
// temps : reformée au temps fort suivant, flash (confort).
//
// Debug : ?dissolve=wind|explode|vortex|rain|pixels|sand (mode fixe),
// ?dissolvehold=1 (l'image reste formée : netteté), ?dissolveprobe=1
// (window.__dissolve).

const TIER = {
  0: { N: 128, rot3d: false },
  1: { N: 192, rot3d: true },
  2: { N: 256, rot3d: true }
};
const IMG_H = 1.4;                       // l'image formée : 70 % de la hauteur (écran = 2)
const FOV = 30;
const CAM_D = 1 / Math.tan(FOV / 2 * Math.PI / 180);
const NO_BPM = 124;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const BLOCK = 16;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// ------------------------------------------------------------------ GLSL
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// la mise à jour : MRT (position, vitesse)
const SIM_VERTEX = `
  precision highp float;
  in vec3 position;
  in vec2 uv;
  out vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const SIM_FRAGMENT = `
  precision highp float;
  uniform sampler2D tPos, tVel;
  uniform float uN, uHalfW;        // la grille, la demi-largeur de l'image
  uniform float uT;                // le temps dans la mesure (temps)
  uniform float uDt;               // le pas (temps)
  uniform float uTime;
  uniform int uMode;
  uniform float uInit, uHold, uBuild, uDrop, uDropT, uWindDir;
  in vec2 vUv;
  layout(location = 0) out vec4 oPos;
  layout(location = 1) out vec4 oVel;
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  vec3 hash3(vec2 p) { return vec3(hash(p), hash(p + 17.1), hash(p + 31.7)); }
  // un bruit de curl bon marché (dérivées de sinus) : divergence nulle
  vec3 curl(vec3 p) {
    float a = sin(p.y * 2.1 + p.z * 1.3) * 0.5 + sin(p.y * 4.3 - p.x * 2.7) * 0.25;
    float b = sin(p.z * 2.3 + p.x * 1.7) * 0.5 + sin(p.z * 3.9 - p.y * 2.1) * 0.25;
    float c = sin(p.x * 1.9 + p.y * 2.9) * 0.5 + sin(p.x * 3.7 - p.z * 2.3) * 0.25;
    return vec3(b - c, c - a, a - b);
  }
  float smooth01(float x) { x = clamp(x, 0.0, 1.0); return x * x * (3.0 - 2.0 * x); }
  void main() {
    vec2 cell = floor(vUv * uN);
    vec3 o = vec3((vUv.x * 2.0 - 1.0) * uHalfW, (vUv.y * 2.0 - 1.0) * ${(IMG_H / 2).toFixed(3)}, 0.0);
    vec3 h3 = hash3(cell + 0.5);
    float h = h3.x;
    vec4 P = texture(tPos, vUv), V = texture(tVel, vUv);          // (GLSL ES 3 : texture, pas texture2D)
    vec3 p = P.xyz, v = V.xyz;
    if (uInit > 0.5 || uHold > 0.5) { oPos = vec4(o, 0.0); oVel = vec4(0.0); return; }
    float r = length(vec2(o.x / uHalfW, o.y / ${(IMG_H / 2).toFixed(3)}));
    // LE DÉLAI de libération selon le mode (temps)
    float delay = 0.0;
    if (uMode == 0) delay = clamp(((uWindDir > 0.5 ? o.x : -o.x) + uHalfW) / (2.0 * uHalfW) * 1.2 + (h - 0.5) * 0.25, 0.0, 1.4);
    else if (uMode == 1) delay = min(0.5, r * 0.45);
    else if (uMode == 2) delay = min(1.0, r * 0.7 + h * 0.15);
    else if (uMode == 3) delay = min(1.4, h3.y * 1.0 + (${(IMG_H / 2).toFixed(3)} - o.y) / ${IMG_H.toFixed(3)} * 0.4);
    else if (uMode == 4) delay = min(1.2, hash(floor(cell / ${BLOCK.toFixed(1)}) + 3.0) * 1.2);
    else delay = clamp((${(IMG_H / 2).toFixed(3)} - o.y) / ${IMG_H.toFixed(3)} * 1.3 + (h - 0.5) * 0.2, 0.0, 1.4);
    if (uDrop > 0.5) delay = min(0.35, r * 0.3);
    float dt = uDt;
    // (le drop : libres pendant sept temps, le ressort les ramène sur le dernier, recalage à la fin)
    float dissolveEnd = uDrop > 0.5 ? 7.0 : ${RETURN_START.toFixed(1)};
    bool free = uT >= delay && uT < dissolveEnd;
    // la montée : des particules se détachent (de plus en plus), l'image vibre
    bool buildFree = uBuild > 0.01 && h3.z < uBuild * 0.35 && uT < 3.0;
    if (uMode == 4 && uDrop < 0.5) {
      // PIXELS, cinématique : le bloc tourne autour de son centre, puis se déroule
      vec2 bc = (floor(cell / ${BLOCK.toFixed(1)}) + 0.5) * ${BLOCK.toFixed(1)} / uN;
      vec3 c = vec3((bc.x * 2.0 - 1.0) * uHalfW, (bc.y * 2.0 - 1.0) * ${(IMG_H / 2).toFixed(3)}, 0.0);
      vec3 bh = hash3(floor(cell / ${BLOCK.toFixed(1)}) + 7.0);
      vec3 axis = normalize(vec3(bh.x - 0.5, bh.y - 0.5, (bh.z - 0.5) * 0.4));
      float up = smooth01((uT - delay) / 1.2), down = smooth01((uT - 2.0 - delay * 0.3) / 1.2);   // (déroulé avant 3,6 : le recalage finit à 3,9)
      float ang = 3.14159 * (up - down) * (bh.z > 0.5 ? 1.0 : -1.0);
      vec3 d = o - c;
      float ca = cos(ang), sa = sin(ang);
      vec3 rot = d * ca + cross(axis, d) * sa + axis * dot(axis, d) * (1.0 - ca);
      vec3 lift = axis * 0.0 + vec3(0.0, 0.0, 0.35) * sin(3.14159 * (up - down));
      vec3 np = c + rot + lift;
      v = (np - p) / max(dt, 1e-4);
      p = np;
    } else {
      if (free || buildFree) {
        vec3 a = vec3(0.0);
        if (uDrop > 0.5 || uMode == 1) {
          // EXPLOSION : vitesse radiale au départ, puis dérive et rotation
          if (length(v) < 0.01) { vec3 dir = normalize(vec3(o.xy, (h3.y - 0.5) * 0.8) + 1e-4); v = dir * (1.8 + 1.4 * h3.z) * (uDrop > 0.5 ? 1.4 : 1.0); }
          a = curl(p * 2.0 + uTime * 0.3) * 0.8 - v * 0.9;
          if (uDrop > 0.5) a += -p * 0.6 + curl(p * 1.5 - uTime * 0.2) * 0.6;   // le nuage reste groupé, tourbillonne
        } else if (uMode == 0) {
          // VENT : emportées d'un bord à l'autre par un bruit de curl
          vec3 wind = vec3(uWindDir > 0.5 ? 1.6 : -1.6, 0.25 * sin(uTime * 1.7 + o.y * 3.0), 0.2);
          a = (wind - v) * 2.5 + curl(p * 2.5 + uTime * 0.6) * 2.2;
        } else if (uMode == 2) {
          // TOURBILLON : vortex autour de l'axe vertical, portance
          vec3 rel = vec3(p.x, 0.0, p.z);
          float rr = max(length(rel), 0.05);
          vec3 tang = vec3(-rel.z, 0.0, rel.x) / rr;
          a = tang * 6.0 / (0.4 + rr) + (0.5 - rr) * rel / rr * 2.0 + vec3(0.0, 1.2, 0.0) - v * 1.2 + curl(p * 3.0) * 0.6;
        } else if (uMode == 3) {
          // PLUIE : chute par colonnes, rebond au sol
          a = vec3(0.0, -6.0, 0.0) + curl(p * 2.0 + uTime) * 0.15 - v * 0.2;
        } else {
          // SABLE : s'effrite depuis le haut, tombe en volutes
          a = vec3(0.3 * sin(uTime * 2.0 + o.y * 5.0), -2.5, 0.0) + curl(p * 3.0 + uTime * 0.8) * 1.8 - v * 0.8;
        }
        if (buildFree && !free) a += (o - p) * 3.0 + vec3(sin(uTime * 40.0 + h * 50.0), cos(uTime * 37.0 + h * 40.0), 0.0) * 0.6 * uBuild;
        v += a * dt;
        p += v * dt;
        if (uMode == 3 && p.y < -1.0) { p.y = -1.0; v.y = abs(v.y) * 0.45; v.xz *= 0.8; }
      } else if (uT >= dissolveEnd || (!free && uT < delay && length(p - o) > 1e-6)) {
        // LE RETOUR : ressort critique après un délai lié à la distance (la vague), puis recalage
        float dist = length(p - o);
        float rd = min(0.7, dist * 0.4);
        if (uT >= ${RETURN_START.toFixed(1)} + rd || uT < delay) {
          vec3 acc = -${OMEGA.toFixed(1)} * ${OMEGA.toFixed(1)} * (p - o) - 2.0 * ${OMEGA.toFixed(1)} * v;
          v += acc * dt; p += v * dt;
        }
      }
      // la montée : vibration de l'image formée
      if (uBuild > 0.01 && length(p - o) < 0.002) p = o + vec3(sin(uTime * 53.0 + h * 60.0), cos(uTime * 47.0 + h * 70.0), 0.0) * 0.0018 * uBuild;   // (bornée à 1 px)
    }
    // le recalage (tous les modes) : la fin de la mesure arrive exactement sur l'origine
    float snapT = uDrop > 0.5 ? uDropT : uT;
    float snap = (snapT - ${SNAP_START.toFixed(2)}) / ${SNAP_BEATS.toFixed(2)};
    if (snap > 0.0 && (uDrop < 0.5 || uDropT > 3.0)) { float k = min(1.0, snap); p += (o - p) * k; v *= (1.0 - k); }
    if (length(p - o) < 0.0008 && length(v) < 0.02) { p = o; v = vec3(0.0); }
    oPos = vec4(p, free ? 1.0 : 0.0);
    oVel = vec4(v, delay);
  }
`;
// le rendu : des carrés exacts formés (calés sur les pixels), des points ronds lumineux en vol
const POINT_VERTEX = `
  attribute vec2 aRef;
  uniform sampler2D tPos;
  uniform sampler2D uCell;         // la pochette réduite en N x N : la moyenne de chaque cellule
  uniform float uHasCover, uN, uHalfW;
  uniform vec2 uRes;
  uniform vec2 uCenter;            // le centre de composition (unités de hauteur)
  uniform float uAspect, uCamD, uZoom, uRoll, uCloudRot, uStyle, uWaveR, uWaveAmp, uGain, uTrailPass;
  uniform vec3 uNeon;
  varying vec3 vCol;
  varying float vFlight, vSize;
  void main() {
    vec4 P = texture2D(tPos, aRef);
    vec3 p = P.xyz;
    vec3 o = vec3((aRef.x * 2.0 - 1.0) * uHalfW, (aRef.y * 2.0 - 1.0) * ${(IMG_H / 2).toFixed(3)}, 0.0);
    float away = length(p - o);
    float flight = smoothstep(0.004, 0.03, away);
    // la couleur : la moyenne de la cellule (jamais un texel) ; sans pochette, un dégradé
    vec3 cov = texture2D(uCell, aRef).rgb;
    vec3 col = uHasCover > 0.5 ? cov : mix(vec3(0.9, 0.2, 0.6), vec3(0.2, 0.7, 1.0), aRef.y) * (0.6 + 0.4 * aRef.x);
    // les styles ne touchent que le vol : formée, l'image garde ses couleurs
    float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
    vec3 styled = col;
    if (uStyle > 0.5 && uStyle < 1.5) styled = uNeon * pow(lum, 0.8) * 1.3;
    if (uStyle > 1.5) styled = col * 1.35;
    col = mix(col, styled * 1.45 + 0.05, flight);
    // l'onde du temps : une ondulation de luminance sur l'image formée (pas de profondeur : des
    // rangées rétrécies laissaient des lignes)
    float wr = length(o.xy);
    col *= 1.0 + 0.22 * uWaveAmp * exp(-pow((wr - uWaveR) / 0.12, 2.0)) * (1.0 - flight);
    // le nuage du drop tourne autour de l'axe vertical du centre
    float c = cos(uCloudRot), s = sin(uCloudRot);
    p = vec3(p.x * c + p.z * s, p.y, -p.x * s + p.z * c);
    // la caméra virtuelle : perspective, champ 30°, l'écran fait 2 de haut en z = 0
    float depth = uCamD - p.z;
    float persp = uCamD / max(depth, 0.1) * uZoom;
    vec2 scr = p.xy * persp;
    float cr = cos(uRoll), sr = sin(uRoll);
    scr = vec2(scr.x * cr - scr.y * sr, scr.x * sr + scr.y * cr) + uCenter;
    vec2 ndc = vec2(scr.x / uAspect, scr.y);
    // la taille : la cellule exacte, arrondie au pixel supérieur ; formée, le centre est calé sur
    // la grille des pixels (les carrés pavent sans fente ni grain)
    float cellPx = ${IMG_H.toFixed(3)} / uN * (uRes.y * 0.5) * persp;
    vec2 px = (ndc * 0.5 + 0.5) * uRes;
    vec2 snapped = floor(px) + 0.5;
    px = mix(snapped, px, flight);
    ndc = px / uRes * 2.0 - 1.0;
    gl_Position = vec4(ndc, (depth - 0.5) / (uCamD * 2.0), 1.0);
    float formedPx = ceil(cellPx - 0.01);
    float sz = mix(formedPx, cellPx * 1.35, flight) * (uStyle > 1.5 ? mix(1.0, 0.75, flight) : 1.0);
    if (uTrailPass > 0.5) { sz = cellPx * 1.3; if (flight < 0.05) gl_Position = vec4(2.0, 2.0, 2.0, 1.0); }
    gl_PointSize = max(sz, 1.0);
    vSize = sz;
    vCol = col * uGain; vFlight = flight;
  }
`;
const POINT_FRAGMENT = `
  precision highp float;
  uniform float uTrailPass;
  varying vec3 vCol;
  varying float vFlight, vSize;
  void main() {
    // formée : le carré plein ; en vol : un disque doux ; dans le buffer de traînées : plus doux, plus faible
    vec2 q = gl_PointCoord - 0.5;
    float d = length(q) * 2.0;
    float disc = 1.0 - smoothstep(0.75, 1.0, d);
    float a = mix(1.0, disc, vFlight);
    if (uTrailPass > 0.5) { float soft = 1.0 - smoothstep(0.1, 1.0, d); gl_FragColor = vec4(vCol * soft * 0.045, 1.0); return; }   // (0,045 / (1 − 0,88) : les traînées plafonnent sous le point lui-même)
    if (a < 0.3) discard;
    gl_FragColor = vec4(vCol * mix(1.0, 1.0 + 0.4 * (1.0 - d), vFlight), 1.0);
  }
`;
// la pochette formée en un seul quad, par la même projection que les points
const COVER_VERTEX = `
  uniform float uHalfW, uAspect, uCamD, uZoom, uRoll;
  uniform vec2 uCenter;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec2 p = vec2((uv.x * 2.0 - 1.0) * uHalfW, (uv.y * 2.0 - 1.0) * ${(IMG_H / 2).toFixed(3)});
    float persp = uZoom;
    vec2 scr = p * persp;
    float cr = cos(uRoll), sr = sin(uRoll);
    scr = vec2(scr.x * cr - scr.y * sr, scr.x * sr + scr.y * cr) + uCenter;
    gl_Position = vec4(scr.x / uAspect, scr.y, (uCamD - 0.5) / (uCamD * 2.0), 1.0);
  }
`;
const COVER_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uCover;
  uniform float uGain;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uCover, vUv).rgb * uGain, 1.0); }
`;
// la réduction N x N de la pochette (mipmaps : chaque texel est la moyenne de sa cellule)
const CELL_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uCover;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uCover, vUv).rgb, 1.0); }
`;
// le flou gaussien séparable (9 prises)
const BLUR_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform vec2 uStep;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uTex, vUv).rgb * 0.2270;
    c += (texture2D(uTex, vUv + uStep * 1.3846).rgb + texture2D(uTex, vUv - uStep * 1.3846).rgb) * 0.3162;
    c += (texture2D(uTex, vUv + uStep * 3.2308).rgb + texture2D(uTex, vUv - uStep * 3.2308).rgb) * 0.0703;
    gl_FragColor = vec4(c, 1.0);
  }
`;
// le fond : la pochette floue en mode couvrir (R18), sombre, vignettée ; redessiné à chaque image
const BG_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uBlur;
  uniform float uHasCover, uAspect, uCoverAspect, uGain;
  uniform vec2 uCenter;
  varying vec2 vUv;
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uCenter;
    float sc = max(2.0 * uAspect / uCoverAspect, 2.0) * 0.5;
    vec2 uv = clamp(s / vec2(sc * uCoverAspect, sc) + 0.5, 0.0, 1.0);
    vec3 c = uHasCover > 0.5 ? texture2D(uBlur, uv).rgb : vec3(0.08, 0.03, 0.1);
    float vig = 1.0 - 0.35 * smoothstep(0.45, 1.25, length(s) / max(uAspect, 1.0));
    gl_FragColor = vec4(c * 0.30 * vig * uGain, 1.0);
  }
`;
// les traînées : décroissance qui atteint zéro (le facteur, puis 2/255 de moins), dans un buffer
// qui ne contient que les particules en vol
const TRAIL_DECAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uK;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(max(vec3(0.0), texture2D(uTex, vUv).rgb * uK - 2.0 / 255.0), 1.0); }
`;
const TRAIL_SHOW_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;
const LUM_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { vec3 c = texture2D(uTex, vUv).rgb; gl_FragColor = vec4(vec3(pow(dot(c, vec3(0.2126, 0.7152, 0.0722)), 1.0 / 2.2)), 1.0); }
`;

// =============================================================================

export class DissolveVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._L = TIER[this._tier] || TIER[1];
    const fm = urlParam('dissolve');
    this._forceMode = MODES.includes(fm) ? fm : null;
    this._hold = urlParam('dissolvehold') === '1';
    this._post = new VdjPost({ bloomStrength: 0.5, bloomThreshold: 0.8, grain: 0.006, vignette: 0.4, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._postValue = this._post.update(1);
    this._comfort = new VdjComfort();
    if (urlParam('dissolveprobe') === '1' && typeof window !== 'undefined') window.__dissolve = this;
  }

  get postProcessing() { return this._postValue; }

  init(scene) {
    this._sceneRef = scene;
    const N = this._L.N;
    this._size = new THREE.Vector2(1280, 720);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    // la simulation : deux cibles MRT (position, vitesse), ping-pong
    const mk = () => new THREE.WebGLRenderTarget(N, N, { count: 2, type: THREE.FloatType, format: THREE.RGBAFormat, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._sim = [mk(), mk()]; this._cur = 0;
    this._SU = { tPos: { value: null }, tVel: { value: null }, uN: { value: N }, uHalfW: { value: IMG_H / 2 }, uT: { value: 0 }, uDt: { value: 0 }, uTime: { value: 0 }, uMode: { value: 0 },
      uInit: { value: 1 }, uHold: { value: this._hold ? 1 : 0 }, uBuild: { value: 0 }, uDrop: { value: 0 }, uDropT: { value: 0 }, uWindDir: { value: 1 } };
    this._simMat = new THREE.RawShaderMaterial({ glslVersion: THREE.GLSL3, uniforms: this._SU, vertexShader: SIM_VERTEX, fragmentShader: SIM_FRAGMENT, depthTest: false, depthWrite: false });
    this._simScene = new THREE.Scene();
    const sq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._simMat); sq.frustumCulled = false; this._simScene.add(sq);
    // les points
    const refs = new Float32Array(N * N * 2), dummy = new Float32Array(N * N * 3);
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) { const k = j * N + i; refs[2 * k] = (i + 0.5) / N; refs[2 * k + 1] = (j + 0.5) / N; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(dummy, 3));
    g.setAttribute('aRef', new THREE.BufferAttribute(refs, 2));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    // la pochette réduite en N x N (la moyenne de chaque cellule) : calculée une fois par pochette
    this._cellTarget = new THREE.WebGLRenderTarget(N, N, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false });
    this._PU = { tPos: { value: null }, uCell: { value: this._cellTarget.texture }, uHasCover: { value: 0 }, uN: { value: N }, uHalfW: { value: IMG_H / 2 }, uRes: { value: this._size },
      uCenter: { value: new THREE.Vector2() }, uAspect: { value: 1.78 }, uCamD: { value: CAM_D }, uZoom: { value: 1 }, uRoll: { value: 0 }, uCloudRot: { value: 0 }, uStyle: { value: 0 },
      uWaveR: { value: -1 }, uWaveAmp: { value: 0 }, uGain: { value: 1 }, uNeon: { value: new THREE.Vector3(1, 0.3, 0.8) }, uTrailPass: { value: 0 } };
    this._points = new THREE.Points(g, new THREE.ShaderMaterial({ uniforms: this._PU, vertexShader: POINT_VERTEX, fragmentShader: POINT_FRAGMENT, depthTest: true, depthWrite: true }));
    this._points.frustumCulled = false; this._points.renderOrder = 2;
    this._pscene = new THREE.Scene(); this._pscene.add(this._points);
    // le même nuage, dessiné dans le buffer de traînées (seules les particules en vol y entrent)
    this._TPU = Object.assign({}, this._PU, { uTrailPass: { value: 1 }, uRes: { value: new THREE.Vector2(640, 360) } });
    this._trailPoints = new THREE.Points(g, new THREE.ShaderMaterial({ uniforms: this._TPU, vertexShader: POINT_VERTEX, fragmentShader: POINT_FRAGMENT, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending }));
    this._trailPoints.frustumCulled = false;
    this._trailScene = new THREE.Scene(); this._trailScene.add(this._trailPoints);
    const mkT = (w, h, lin) => new THREE.WebGLRenderTarget(w, h, { depthBuffer: false, stencilBuffer: false, minFilter: lin ? THREE.LinearFilter : THREE.NearestFilter, magFilter: lin ? THREE.LinearFilter : THREE.NearestFilter, generateMipmaps: false });
    this._trail = [mkT(640, 360, true), mkT(640, 360, true)]; this._trailCur = 0;
    this._quadScene = new THREE.Scene();
    this._quadMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), null); this._quadMesh.frustumCulled = false; this._quadScene.add(this._quadMesh);
    this._mats = {
      cell: new THREE.ShaderMaterial({ uniforms: { uCover: { value: null } }, vertexShader: QUAD_VERTEX, fragmentShader: CELL_FRAGMENT, depthTest: false, depthWrite: false }),
      blur: new THREE.ShaderMaterial({ uniforms: { uTex: { value: null }, uStep: { value: new THREE.Vector2() } }, vertexShader: QUAD_VERTEX, fragmentShader: BLUR_FRAGMENT, depthTest: false, depthWrite: false }),
      decay: new THREE.ShaderMaterial({ uniforms: { uTex: { value: null }, uK: { value: 0.88 } }, vertexShader: QUAD_VERTEX, fragmentShader: TRAIL_DECAY_FRAGMENT, depthTest: false, depthWrite: false })
    };
    // la chaîne du fond : 1/2, 1/4, 1/8 d'un 1280 x 720, puis le flou séparable
    this._blurChain = [mkT(640, 360, true), mkT(320, 180, true), mkT(160, 90, true), mkT(160, 90, true)];
    this._blurDone = null;
    this._BU = { uBlur: { value: this._blurChain[2].texture }, uHasCover: { value: 0 }, uAspect: { value: 1.78 }, uCoverAspect: { value: 1 }, uGain: { value: 1 }, uCenter: { value: new THREE.Vector2() } };
    const bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._BU, vertexShader: QUAD_VERTEX, fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false }));
    bg.frustumCulled = false; bg.renderOrder = 0; this._pscene.add(bg);
    this._trailShowU = { uTex: { value: this._trail[0].texture } };
    const ts = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._trailShowU, vertexShader: QUAD_VERTEX, fragmentShader: TRAIL_SHOW_FRAGMENT, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending }));
    ts.frustumCulled = false; ts.renderOrder = 1; this._pscene.add(ts);
    // la pochette formée en un quad, parfaitement nette
    this._CU = { uCover: { value: null }, uHalfW: this._PU.uHalfW, uAspect: this._PU.uAspect, uCamD: this._PU.uCamD, uZoom: this._PU.uZoom, uRoll: this._PU.uRoll, uCenter: this._PU.uCenter, uGain: this._PU.uGain };
    this._coverQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._CU, vertexShader: COVER_VERTEX, fragmentShader: COVER_FRAGMENT, depthTest: false, depthWrite: false }));
    this._coverQuad.frustumCulled = false; this._coverQuad.renderOrder = 3; this._coverQuad.visible = false; this._pscene.add(this._coverQuad);
    this._pcam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);   // (les points calculent leur propre projection)
    // la cible privée et l'affichage
    this._target = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: true, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
    this._dispU = { uTex: { value: this._target.texture } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: this._target.texture } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: QUAD_VERTEX, fragmentShader: LUM_FRAGMENT, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false; this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.2; this._lumPending = false; this._lumAvg = 0.2; this._gain = 1; this._gainRendered = 1;

    // l'état
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._paused = false; this._init = true;
    this._mode = this._forceMode || 'wind'; this._modeHistory = [this._mode]; this._rng = 777;
    this._style = 0; this._build = 0; this._low = 0.5; this._energy = 0.5;
    this._dropAt = null; this._cloudRot = 0; this._beatAt = -99;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._coverUrl = undefined; this._coverTex = null; this._coverAspect = 1; this._paletteSeen = undefined;
    this._stats = { beats: 0, bars: 0, phrases: 0, drops: 0, modes: {} };
    this._applyPalette(null);
  }

  _rand() { this._rng = (this._rng * 1664525 + 1013904223) >>> 0; return this._rng / 4294967296; }

  _applyPalette(pal) {
    if (pal === this._paletteSeen && pal !== null) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6];
    const c = vdjColor(v[0]);
    this._PU.uNeon.value.set(c.r, c.g, c.b);
  }
  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._PU.uHasCover.value = 0; this._BU.uHasCover.value = 0; this._coverQuad.visible = false;
    if (!profile.artworkUrl) return;
    const url = profile.artworkUrl;
    loadArtworkTexture(url, { mipmaps: true, wrap: THREE.ClampToEdgeWrapping }, (tex) => {
      if (!this._sceneRef || url !== this._coverUrl) { tex.dispose(); return; }
      tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true;      // (la pochette est en sRGB : sans ce drapeau l'image formée sortait trop claire)
      this._coverTex = tex; this._CU.uCover.value = tex; this._PU.uHasCover.value = 1; this._BU.uHasCover.value = 1; this._blurDone = null;
      const img = tex.image; this._coverAspect = img && img.width ? img.width / img.height : 1;
      this._BU.uCoverAspect.value = this._coverAspect;
      // l'image formée : 70 % de la hauteur, largeur selon la pochette (bornée à 1,0 de demi-largeur)
      this._SU.uHalfW.value = this._PU.uHalfW.value = Math.min(1.0, IMG_H / 2 * this._coverAspect);
    }, () => {});
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    this._loadCover(profile);
    if (this._paused) { this._dtBeats = 0; return; }           // pause : gel (la simulation ne fait aucun pas)
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._time += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) { if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++; this._phaseBar = barIn; target = 4 * (this._barCount + barIn); }
      else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock, beatS = 60 / this._bpm;
    this._dtBeats = Math.min(0.25, clock - this._clockPrev);
    const beat = Math.floor(clock) !== Math.floor(this._clockPrev);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);
    const barPhase = clock / 4 - Math.floor(clock / 4), barIdx = Math.floor(clock / 4);
    const barT = barPhase * 4;

    const director = profile.director, bands = director && director.bands;
    this._low += ((bands && bands.low > 0 ? clamp01(bands.low) : 0.6) - this._low) * (1 - Math.exp(-dt / 0.1));
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.6;
    this._energy += (energy - this._energy) * (1 - Math.exp(-dt / 2));
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && (director.mode === 'build' || director.mode === 'buildup')) build = Math.max(build, 0.7);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));

    // LE DROP : explosion totale, nuage 3D deux mesures, retour sur le dernier temps
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && this._dropAt == null) {
      this._dropAt = 4 * Math.floor(clock / 4); this._stats.drops++;    // calé sur la mesure : huit temps, reformée au temps fort
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    // (le drop couvre les deux mesures depuis le début de la mesure courante ; sa ligne de temps va de 0 à 8)
    const dropT = this._dropAt != null ? clock - this._dropAt : -1;
    if (this._dropAt != null && dropT >= 8) this._dropAt = null;
    const inDrop = this._dropAt != null;

    if (bar) {
      this._stats.bars++;
      if (!this._forceMode && !inDrop) { this._mode = nextMode(this._modeHistory, this._rand(), this._energy); this._stats.modes[this._mode] = (this._stats.modes[this._mode] || 0) + 1; this._modeHistory.push(this._mode); if (this._modeHistory.length > 16) this._modeHistory.shift(); }
      this._SU.uWindDir.value = this._rand() < 0.5 ? 0 : 1;
    }
    if (phrase) { this._stats.phrases++; this._style = (this._style + 1) % 3; }
    if (beat) { this._stats.beats++; this._beatAt = clock; }
    // le nuage du drop tourne (tier 0 : pas de rotation 3D)
    if (inDrop && this._L.rot3d) this._cloudRot += dt * 0.9 * Math.min(1, dropT / 0.5) * (dropT < 7 ? 1 : Math.max(0, 7.8 - dropT) / 0.8);
    else this._cloudRot *= Math.exp(-dt / 0.3);

    const SU = this._SU;
    SU.uT.value = inDrop ? dropT : barT;                       // (pendant le drop : la ligne de temps court sur huit temps)
    SU.uDt.value = this._dtBeats; SU.uTime.value = this._time; SU.uMode.value = inDrop ? 1 : MODE_ID[this._mode];
    SU.uBuild.value = this._build; SU.uDrop.value = inDrop ? 1 : 0;
    SU.uDropT.value = inDrop ? Math.max(0, dropT - 4) : 0;    // (le recalage du drop : sur les 0,3 derniers de ses 8 temps, à l'échelle 0..4)
    const PU = this._PU;
    const kick = hasGrid ? vdjCameraKick(barPhase, barIdx, 4) : { zoom: 1, roll: 0 };
    PU.uZoom.value = kick.zoom; PU.uRoll.value = kick.roll; PU.uCloudRot.value = this._cloudRot;
    PU.uStyle.value = this._style;
    const sinceBeat = (clock - this._beatAt) * beatS;
    PU.uWaveR.value = sinceBeat / (beatS * 0.8) * 1.2;
    PU.uWaveAmp.value = hasGrid ? 0.12 * (0.5 + this._low) : 0.05;
    const sc = safeZoneUniforms.uStageCenter.value;
    PU.uCenter.value.set(sc.x, sc.y); this._BU.uCenter.value.set(sc.x, sc.y);
    PU.uAspect.value = this._BU.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;
    // R10
    this._lumAge += dt;
    if (this._lumAge >= 0.5) { this._lumAge = 0; this._lumPending = true; }
    this._lumAvg += (this._lumMeasured - this._lumAvg) * Math.min(1, dt / 1.0);
    const gt = Math.min(1, 0.36 / Math.max(1e-3, this._lumAvg));
    this._gain += (gt - this._gain) * Math.min(1, dt / 0.5);
    PU.uGain.value = this._BU.uGain.value = this._gain;
    this._postValue = this._post.update(hasGrid ? vdjBeatChroma(clock - Math.floor(clock), beatS) : 1);
    this._barT = barT; this._inDrop = inDrop; this._dropT = dropT;
    // toutes les particules à leur place : après le recalage, hors montée et hors drop (ou ?dissolvehold)
    this._formedAll = this._hold || (!inDrop && this._build < 0.01 && barT >= SNAP_START + SNAP_BEATS);
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x)), h = Math.max(16, Math.round(this._size.y));
    if (this._target.width !== w || this._target.height !== h) this._target.setSize(w, h);
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    // la pochette : sa réduction N x N et son flou, une fois par pochette
    if (this._coverTex && this._blurDone !== this._coverTex) {
      this._blurDone = this._coverTex;
      const q = this._quadMesh, M = this._mats;
      q.material = M.cell; M.cell.uniforms.uCover.value = this._coverTex;
      renderer.setRenderTarget(this._cellTarget); renderer.render(this._quadScene, this._ortho);
      const ch = this._blurChain;
      renderer.setRenderTarget(ch[0]); renderer.render(this._quadScene, this._ortho);          // 1/2 (mipmaps : propre)
      M.cell.uniforms.uCover.value = ch[0].texture; renderer.setRenderTarget(ch[1]); renderer.render(this._quadScene, this._ortho);   // 1/4 (LINEAR)
      M.cell.uniforms.uCover.value = ch[1].texture; renderer.setRenderTarget(ch[2]); renderer.render(this._quadScene, this._ortho);   // 1/8
      q.material = M.blur;
      for (let i = 0; i < 2; i++) {
        M.blur.uniforms.uTex.value = ch[2].texture; M.blur.uniforms.uStep.value.set(1 / 160, 0); renderer.setRenderTarget(ch[3]); renderer.render(this._quadScene, this._ortho);
        M.blur.uniforms.uTex.value = ch[3].texture; M.blur.uniforms.uStep.value.set(0, 1 / 90); renderer.setRenderTarget(ch[2]); renderer.render(this._quadScene, this._ortho);
      }
      M.cell.uniforms.uCover.value = this._coverTex;
    }
    // la simulation : un pas (aucun en pause ; l'initialisation pose chaque particule à son origine)
    if (!this._paused || this._init) {
      const src = this._sim[this._cur], dst = this._sim[this._cur ^ 1];
      this._SU.tPos.value = src.textures[0]; this._SU.tVel.value = src.textures[1];
      this._SU.uInit.value = this._init ? 1 : 0;
      renderer.setRenderTarget(dst); renderer.render(this._simScene, this._ortho);
      this._cur ^= 1; this._init = false;
    }
    this._PU.tPos.value = this._sim[this._cur].textures[0];
    this._gainRendered = this._gain;
    // les traînées : décroissance vers zéro (vidées en 0,3 s dès que l'image est formée), puis le vol
    if (!this._paused) {
      const src = this._trail[this._trailCur], dst = this._trail[this._trailCur ^ 1];
      this._quadMesh.material = this._mats.decay;
      this._mats.decay.uniforms.uTex.value = src.texture; this._mats.decay.uniforms.uK.value = this._formedAll ? 0.5 : 0.88;
      renderer.setRenderTarget(dst); renderer.render(this._quadScene, this._ortho);
      if (!this._formedAll) { this._TPU.tPos.value = this._PU.tPos.value; renderer.render(this._trailScene, this._pcam); }
      this._trailCur ^= 1;
      this._trailShowU.uTex.value = dst.texture;
    }
    // la pochette formée : un quad net à la place des points
    const quad = this._formedAll && this._PU.uHasCover.value > 0.5;
    this._coverQuad.visible = quad; this._points.visible = !quad;
    renderer.setRenderTarget(this._target);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._pscene, this._pcam);
    if (this._lumPending) {
      this._lumPending = false;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let sum = 0; for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      this._lumMeasured = sum / (256 * 255) / Math.max(0.05, this._gainRendered);
    }
    renderer.setRenderTarget(prev);
    renderer.setClearColor(cc, ca);
  }

  // (sonde) : lit les positions et les origines : écart max à l'origine, part des particules formées
  readState(renderer) {
    const N = this._L.N, buf = new Float32Array(N * N * 4);
    renderer.readRenderTargetPixels(this._sim[this._cur], 0, 0, N, N, buf, undefined, 0);
    const hw = this._PU.uHalfW.value;
    let maxD = 0, formed = 0, sumZ = 0, maxZ = -9, minZ = 9;
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const k = (j * N + i) * 4, ox = ((i + 0.5) / N * 2 - 1) * hw, oy = ((j + 0.5) / N * 2 - 1) * IMG_H / 2;
      const d = Math.hypot(buf[k] - ox, buf[k + 1] - oy, buf[k + 2]);
      maxD = Math.max(maxD, d); if (d < 0.004) formed++;
      sumZ += buf[k + 2]; maxZ = Math.max(maxZ, buf[k + 2]); minZ = Math.min(minZ, buf[k + 2]);
    }
    return { maxDist: +maxD.toFixed(5), formedPct: +(formed / (N * N) * 100).toFixed(2), zSpan: +(maxZ - minZ).toFixed(3), zMean: +(sumZ / (N * N)).toFixed(3) };
  }

  // (sonde) : le buffer de traînées courant : maximum et moyenne (0..1)
  readTrailStats(renderer) {
    const rt = this._trail[this._trailCur], w = rt.width, h = rt.height, buf = new Uint8Array(w * h * 4);
    renderer.readRenderTargetPixels(rt, 0, 0, w, h, buf);
    let mx = 0, sum = 0;
    for (let i = 0; i < buf.length; i += 4) { const v = Math.max(buf[i], buf[i + 1], buf[i + 2]); if (v > mx) mx = v; sum += v; }
    return { max: mx / 255, mean: +(sum / (w * h) / 255).toFixed(5) };
  }

  getProbe() {
    return { tier: this._tier, N: this._L.N, mode: this._mode, style: this._style, barT: +(this._barT || 0).toFixed(3), drop: !!this._inDrop, dropT: +(this._dropT || 0).toFixed(2), cloudRot: +this._cloudRot.toFixed(3),
      build: +this._build.toFixed(2), formedAll: !!this._formedAll, lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), hasCover: this._PU.uHasCover.value, halfW: +this._PU.uHalfW.value.toFixed(3), history: this._modeHistory.slice(), stats: Object.assign({}, this._stats) };
  }

  dispose() {
    if (this._sceneRef && this._display) { this._display.onBeforeRender = null; this._sceneRef.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._points) { this._points.geometry.dispose(); this._points.material.dispose(); }
    for (const rt of (this._sim || []).concat([this._target, this._lumTarget, this._cellTarget]).concat(this._trail || []).concat(this._blurChain || [])) if (rt) rt.dispose();
    if (this._mats) for (const k in this._mats) this._mats[k].dispose();
    if (this._trailPoints) this._trailPoints.material.dispose();
    if (this._coverQuad) { this._coverQuad.geometry.dispose(); this._coverQuad.material.dispose(); }
    if (this._simMat) this._simMat.dispose();
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (typeof window !== 'undefined' && window.__dissolve === this) window.__dissolve = null;
    this._sceneRef = null;
  }
}
